import { useListenToServerChangesSubscription } from "@biggeo/bg-server-lib/studio";
import { Stack } from "@biggeo/bg-ui/lab";
import compact from "lodash/compact";
import kebabCase from "lodash/kebabCase";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Routes } from "../../navigation/redux/model";
import DataSamplesContainer from "../../samples/containers/DataSamplesContainer";
import { toasterActions } from "../../toaster/containers/redux/model";
import { getSavedAreaIdFromParams } from "../../utils/utils";
import {
    useAuroraSpatialData,
    useWebSocketConnections,
} from "../hooks/aurora-hooks";
import { MapContextType, useMap } from "../mapbox/context";
import {
    useMapDataConnection,
    useMapDataState,
} from "../mapbox/context/listeners";
import { setDatasetContext } from "../mapbox/context/utils/general-utils";
import { removeMap, resizeMap } from "../mapbox/utils/map-utils";
import MapViewPage from "./MapViewPage";
import { IMapViewWrapper, MapTableTabs, MapTabs } from "./MapViewWrapper";
import SavedViewPage from "./SavedViewPage";

interface IMapContextWrapper extends IMapViewWrapper {}

const MapContextWrapper = ({
    savedViewId,
    currentTab: _currentTab,
    mapTemplateId,
    isFromDataSamples,
    ...props
}: IMapContextWrapper) => {
    const { map, draw, dispatch, datasets } = useMap();
    const toPage = useNavigate();
    const currentTab = _currentTab ?? MapTabs.map;
    const reduxDispatch = useDispatch();
    const { updateDataset } = setDatasetContext();

    const [openSaveViewPopper, setOpenSaveViewPopper] = useState(false);
    const [openGenerateFilePage, setOpenGenerateFilePage] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const savedAreaId = getSavedAreaIdFromParams(searchParams);

    const [tab, setTableTab] = useState(MapTableTabs.map);

    // Creates sockets connections based on Datasets from the context
    const { sockets, getSocketsForDataSources } = useWebSocketConnections(
        compact(
            datasets.map((c) => {
                if (!c.dataSourceConfiguration.serverUrl) return;

                return {
                    id: c.dataSource.id,
                    serverUrl: c.dataSourceConfiguration.serverUrl,
                };
            })
        )
    );

    const { data: statusData } = useListenToServerChangesSubscription({
        variables: {
            channel: "server-changes",
        },
        fetchPolicy: "no-cache",
        shouldResubscribe: true,
    });

    useEffect(() => {
        if (
            statusData?.listenToServerChanges &&
            statusData?.listenToServerChanges.length > 0
        ) {
            statusData.listenToServerChanges.map((c) => {
                const dataset = datasets?.find(
                    (d) => d.dataSource.id === c.dataSourceId
                );

                if (
                    dataset &&
                    c.serverUrl &&
                    dataset.dataSourceConfiguration.serverUrl !== c.serverUrl
                )
                    updateDataset({
                        dataSourceId: dataset.dataSource.id,
                        dataset: {
                            dataSourceConfiguration: { serverUrl: c.serverUrl },
                        },
                    });
            });
        }
    }, [statusData?.listenToServerChanges]);

    // Sends requests to Velocity based on Viewport or Polygons from the context.
    const { channelId } = useMapDataConnection(getSocketsForDataSources);

    // Catches the response from Velocity and sets the socket responses in the context.
    useAuroraSpatialData(sockets);

    // Listens on the datasets array from the Context. If there are no selected datasets, it clears the socket responses state.
    useMapDataState();

    const handleMapTabs = (t: MapTabs) => {
        toPage(`${Routes.mapView}/${mapTemplateId}/${kebabCase(t)}`);
        reduxDispatch(
            toasterActions.openMapPopup({
                sx: { display: "none" },
            })
        );
    };

    const setTab = (
        input: { t: MapTableTabs } & Pick<
            MapContextType,
            "map" | "isMapLoaded" | "draw" | "dispatch"
        >
    ) => {
        const { t, ...params } = input;

        setTableTab(t);

        if (params.map.current && savedViewId && t === MapTableTabs.table) {
            removeMap({
                isSavedViewPage: true,
                ...params,
            });
        }

        if (t === MapTableTabs.map) {
            params.map.current?.on("load", () => {
                resizeMap({ map: params.map, isMapLoaded: params.isMapLoaded });
            });
        }
    };

    return (
        <Stack height="100%" width="100%">
            {isFromDataSamples && (
                <DataSamplesContainer
                    samples={datasets.filter((d) => !!d.isSample)}
                    openGenerateFilePage={openGenerateFilePage}
                    setOpenGenerateFilePage={setOpenGenerateFilePage}
                    channelId={channelId}
                />
            )}
            {!isFromDataSamples &&
                mapTemplateId &&
                (savedViewId ? (
                    <SavedViewPage
                        {...props}
                        savedViewId={savedViewId}
                        currentTab={currentTab}
                        mapTemplateId={mapTemplateId}
                        setSearchParams={setSearchParams}
                        handleMapTabs={handleMapTabs}
                        savedAreaId={savedAreaId}
                        datasets={datasets || []}
                        searchParams={searchParams}
                        tab={tab}
                        setTab={(t) => {
                            setTab({
                                t,
                                map,
                                isMapLoaded: true,
                                draw,
                                dispatch,
                            });
                        }}
                        openSaveViewPopper={openSaveViewPopper}
                        setOpenSaveViewPopper={setOpenSaveViewPopper}
                        openGenerateFilePage={openGenerateFilePage}
                        setOpenGenerateFilePage={setOpenGenerateFilePage}
                        sockets={sockets}
                        channelId={channelId}
                    />
                ) : (
                    <MapViewPage
                        {...props}
                        savedViewId={savedViewId}
                        currentTab={currentTab}
                        mapTemplateId={mapTemplateId}
                        handleMapTabs={handleMapTabs}
                        savedAreaId={savedAreaId}
                        datasets={datasets || []}
                        searchParams={searchParams}
                        setSearchParams={setSearchParams}
                        tab={tab}
                        setTab={(t) => {
                            setTab({
                                t,
                                map,
                                isMapLoaded: true,
                                draw,
                                dispatch,
                            });
                        }}
                        openSaveViewPopper={openSaveViewPopper}
                        setOpenSaveViewPopper={setOpenSaveViewPopper}
                        openGenerateFilePage={openGenerateFilePage}
                        setOpenGenerateFilePage={setOpenGenerateFilePage}
                        sockets={sockets}
                        channelId={channelId}
                    />
                ))}
        </Stack>
    );
};

export default MapContextWrapper;
